import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { throttle } from 'throttle-debounce';

import { useUpdateClubMemberAuxMutation } from '@features/club/api/hooks';
import { MemberAux } from '@features/club/typings';
import { getTournamentPgnLink } from '@features/shared/api/streams';
import {
  ParticipantDto,
  RcfAttributes,
  Tournament,
  TournamentTeam,
} from '@features/shared/api/typings';
import {
  QUERY_KEYS,
  useBanTeamMutation,
  useCreateTournamentTeamMutation,
  useDeleteTournamentTeamMutation,
  useRearrangeParticipantsMutation,
  useRearrangeTeamsMutation,
  useRenameTournamentTeamMutation,
  useTournamentParticipantStatusMutation,
} from '@features/tournament/api/hooks';
import { ParticipantsTab } from '@features/tournament/ui/components/TournamentParticipantsEdit/types';
import { useRcfAttributesMutation } from '@features/user/api/hooks';

export function useMutations(
  clubId: string,
  tournamentId: string,
  tab: ParticipantsTab,
  isModeration: boolean,
  isEditable: boolean,
  isFinished: boolean,
  isTeam: boolean,
) {
  const { mutateAsync: updateParticipant } = useTournamentParticipantStatusMutation(
    clubId,
    tournamentId,
  );
  const { mutateAsync: deleteTeam } = useDeleteTournamentTeamMutation(clubId, tournamentId);
  const { mutateAsync: createTeam } = useCreateTournamentTeamMutation(clubId, tournamentId);
  const { mutateAsync: renameTeam } = useRenameTournamentTeamMutation(clubId, tournamentId);
  const { mutateAsync: rearrange } = useRearrangeParticipantsMutation(clubId, tournamentId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledRearrange = useCallback(throttle(1000, rearrange, { noLeading: true }), [
    rearrange,
  ]);

  const { mutateAsync: rearrangeTeams } = useRearrangeTeamsMutation(clubId, tournamentId);
  const { mutateAsync: updateRcfAttributes } = useRcfAttributesMutation();
  const { mutateAsync: updateMemberAux } = useUpdateClubMemberAuxMutation(clubId);

  const { mutateAsync: banTeam } = useBanTeamMutation(clubId, tournamentId);

  const qc = useQueryClient();
  const invalidator = useRef({
    tournament() {
      qc.invalidateQueries({
        queryKey: QUERY_KEYS.tournamentCard(clubId, tournamentId),
      });
    },
    participants() {
      qc.invalidateQueries({
        queryKey: QUERY_KEYS.tournamentParticipants(clubId, tournamentId),
      });
    },
  });

  const onCreateTeam = useCallback(
    async (newTeamName: string) => {
      await createTeam({ name: newTeamName });
      invalidator.current.tournament();
    },
    [createTeam],
  );

  const onRenameTeam = useCallback(
    async (teamId: string, name: string) => {
      await renameTeam({ teamId, name });
      invalidator.current.tournament();
    },
    [renameTeam],
  );

  const onDeleteTeam = useCallback(
    async (teamId: string) => {
      await deleteTeam({ teamId });
      invalidator.current.tournament();
      invalidator.current.participants();
    },
    [deleteTeam],
  );
  const onDeleteParticipant = useCallback(
    async (userId: string) => {
      await updateParticipant({ userId, action: 'delete' });
      invalidator.current.participants();
    },
    [updateParticipant],
  );
  const onAcceptParticipant = useCallback(
    async (userId: string) => {
      await updateParticipant({ userId, action: 'accept' });
      invalidator.current.participants();
    },
    [updateParticipant],
  );
  const onRemoveParticipant = useCallback(
    async (userId: string) => {
      await updateParticipant({ userId, action: 'remove' });
      invalidator.current.participants();
    },
    [updateParticipant],
  );
  const onBanParticipant = useCallback(
    async (userId: string) => {
      await updateParticipant({ userId, action: 'ban' });
      invalidator.current.participants();
    },
    [updateParticipant],
  );
  const onBanTeam = useCallback(
    async (teamId: string) => {
      await banTeam({ teamId });
      invalidator.current.participants();
    },
    [banTeam],
  );
  const onParticipantPgnDownload = useCallback(
    async (userId: string) => {
      window.open(getTournamentPgnLink(tournamentId, userId), '_blank');
    },
    [tournamentId],
  );
  const onChangeRcfAttributes = useCallback(
    async (value: RcfAttributes) => {
      const valueToSave: Partial<RcfAttributes> = { ...value };
      delete valueToSave.rcfIdApproved;

      await updateRcfAttributes({ items: [value] });
      invalidator.current.participants();
    },
    [updateRcfAttributes],
  );

  // требование к камерам на видеоконтроле: в aux участника клуба, в списке — копия memberAux
  const onCamerasChange = useCallback(
    async (userId: string, aux: MemberAux | null) => {
      await updateMemberAux({ userId, aux });
      invalidator.current.participants();
    },
    [updateMemberAux],
  );

  const onRearrange = useCallback(
    (participants: ParticipantDto[]) => {
      qc.setQueryData(
        QUERY_KEYS.tournamentParticipants(clubId, tournamentId, {
          status: ['Participating'],
        }),
        [...participants],
      );
      throttledRearrange({
        items: participants.map((p) => ({
          userId: p.userId,
          teamId: p.teamId,
          numberInTeam: p.numberInTeam,
          numberInTournament: p.numberInTournament,
        })),
      });
    },
    [clubId, qc, throttledRearrange, tournamentId],
  );

  const onRearrangeTeams = useCallback(
    async (teams: TournamentTeam[]) => {
      qc.setQueryData(
        QUERY_KEYS.tournamentCard(clubId, tournamentId),
        (tournament: Tournament) => ({
          ...tournament,
          teams,
        }),
      );
      await rearrangeTeams({ teamIds: teams.map((t) => t.teamId) });
    },
    [rearrangeTeams, clubId, qc, tournamentId],
  );

  return {
    onCreateTeam: isEditable && isTeam && tab === 'Participating' ? onCreateTeam : undefined,
    onBanTeam: isModeration && isTeam && tab === 'Participating' ? onBanTeam : undefined,
    onRenameTeam: isEditable && isTeam && tab === 'Participating' ? onRenameTeam : undefined,
    onDeleteTeam: isEditable ? onDeleteTeam : undefined,
    onAcceptParticipant:
      isEditable && !isFinished && tab === 'JoinRequest' ? onAcceptParticipant : undefined,
    onRestoreParticipant:
      isEditable && !isFinished && tab === 'Deleted' ? onAcceptParticipant : undefined,
    onDeleteParticipant:
      isEditable && !isFinished && tab !== 'Deleted' ? onDeleteParticipant : undefined,
    onRearrangeParticipants:
      isEditable && !isFinished && tab === 'Participating' ? onRearrange : undefined,
    onRearrangeTeams,
    onRemoveParticipant:
      isModeration && !isFinished && tab === 'Participating' ? onRemoveParticipant : undefined,
    onBanParticipant: isModeration && tab === 'Participating' ? onBanParticipant : undefined,
    onParticipantPgnDownload:
      isModeration && tab === 'Participating' ? onParticipantPgnDownload : undefined,
    onRcfAttributesChange: onChangeRcfAttributes,
    onCamerasChange,
  };
}
